import { Box, Paper, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { TriaxialGeneral } from '../../../types/triaxial';

interface Props {
  general: TriaxialGeneral;
}

const fmt = (v: number | null | undefined, d = 1): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : '—';
};

const mmToCm = (v: number | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n / 10 : null;
};

/**
 * Initial / Post-Consolidation / Final sample properties table.
 *
 * Mirrors the PDF page-1 table. Displayed on the Parameters tab (live
 * preview as the operator types) and again on the Analysis tab.
 */
export const TriaxialSummaryTable = ({ general }: Props) => {
  const theme = useTheme();

  const rows: Array<[string, string, string, string]> = [
    [
      'Diameter D (cm)',
      fmt(mmToCm(general.computedAvgDiameterMm), 2),
      fmt(general.computedPostConsDiameterCm, 2),
      fmt(general.computedFinalDiameterCm, 2),
    ],
    [
      'Height H (cm)',
      fmt(mmToCm(general.computedAvgLengthMm), 2),
      fmt(general.computedPostConsHeightCm, 2),
      fmt(general.computedFinalHeightCm, 2),
    ],
    [
      'Water Content w (%)',
      fmt(general.computedInitialWaterContentPct, 1),
      fmt(general.computedPostConsWaterContentPct, 1),
      fmt(general.finalWaterContentPct, 1),
    ],
    [
      'Bulk Density ρb (g/cm³)',
      fmt(general.computedInitialBulkDensityMgPerM3, 2),
      fmt(general.computedPostConsBulkDensityMgPerM3, 2),
      fmt(general.computedFinalBulkDensityMgPerM3, 2),
    ],
    [
      'Dry Density ρd (g/cm³)',
      fmt(general.computedInitialDryDensityMgPerM3, 2),
      fmt(general.computedPostConsDryDensityMgPerM3, 2),
      fmt(general.computedFinalDryDensityMgPerM3, 2),
    ],
    [
      'Void Ratio e',
      fmt(general.computedInitialVoidRatio, 2),
      fmt(general.computedPostConsVoidRatio, 2),
      fmt(general.computedFinalVoidRatio, 2),
    ],
    [
      'Specific Gravity Gs',
      fmt(general.specificGravityGs, 2),
      fmt(general.specificGravityGs, 2),
      fmt(general.specificGravityGs, 2),
    ],
    [
      'Degree of Saturation Sr (%)',
      fmt(general.computedInitialDegreeOfSatPct, 0),
      fmt(general.computedPostConsDegreeOfSatPct, 0),
      fmt(general.computedFinalDegreeOfSatPct, 0),
    ],
  ];

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', color: 'primary.main', fontWeight: 700 }}>
        SAMPLE PROPERTIES
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '2.4fr 1fr 1.4fr 1fr', gap: 0 }}>
        <Box sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary', p: 0.5, borderBottom: `2px solid ${theme.palette.text.primary}` }} />
        <Box sx={{ fontWeight: 700, fontSize: '0.8rem', textAlign: 'center', p: 0.5, borderBottom: `2px solid ${theme.palette.text.primary}` }}>
          INITIAL
        </Box>
        <Box sx={{ fontWeight: 700, fontSize: '0.8rem', textAlign: 'center', p: 0.5, borderBottom: `2px solid ${theme.palette.text.primary}` }}>
          POST-CONSOLIDATION
        </Box>
        <Box sx={{ fontWeight: 700, fontSize: '0.8rem', textAlign: 'center', p: 0.5, borderBottom: `2px solid ${theme.palette.text.primary}` }}>
          FINAL
        </Box>
        {rows.map(([label, vi, vp, vf]) => (
          <Box
            key={label}
            sx={{
              display: 'contents',
            }}
          >
            <Box sx={{ p: 0.5, fontSize: '0.85rem', borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.1)}` }}>
              {label}
            </Box>
            <Box sx={{ p: 0.5, fontSize: '0.85rem', textAlign: 'center', borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.1)}` }}>
              {vi}
            </Box>
            <Box sx={{ p: 0.5, fontSize: '0.85rem', textAlign: 'center', borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.1)}` }}>
              {vp}
            </Box>
            <Box sx={{ p: 0.5, fontSize: '0.85rem', textAlign: 'center', borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.1)}` }}>
              {vf}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};
